import React, { useState, useEffect, useRef } from 'react'
import {
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Grid,
  InputAdornment,
  Box,
  Typography,
} from '@mui/material'
import { makeStyles } from '../../utils/hooks/makeStyles'

const useStyles = makeStyles((theme) => ({
  root: {
    width: '100%',
  },
  inputGrid: {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    gap: 8,
  },
  input: {
    flex: 1,
    minWidth: 80,
  },

  toggleButton: {
    // padding: '8px 16px',
    fontSize: '12px',
    minWidth: '60px',
    '&.Mui-selected': {
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.primary.contrastText,
      '&:hover': {
        backgroundColor: theme.palette.primary.dark,
      },
    },
  },
}))

function cmToFeetInches(cm: number) {
  const totalInches = cm / 2.54
  const feet = Math.floor(totalInches / 12)
  const inches = Math.round(totalInches % 12)
  return { feet, inches }
}

function feetInchesToCm(feet: number, inches: number) {
  return Math.round((feet * 12 + inches) * 2.54 * 10) / 10
}

interface HeightInputProps {
  value: number // always in cm
  onChange: (cm: number) => void
  label?: string
  disabled?: boolean
  required?: boolean
  error?: boolean
  helperText?: string
  defaultDisplayMode?: 'cm' | 'ftin'
}

const Height: React.FC<HeightInputProps> = ({
  value,
  onChange,
  label,
  disabled,
  required = false,
  error = false,
  helperText = '',
  defaultDisplayMode = 'cm',
}) => {
  const classes = useStyles()
  const [displayMode, setDisplayMode] = useState<'cm' | 'ftin'>(
    defaultDisplayMode
  )
  const [cmInput, setCmInput] = useState<string>('')
  const [feetInput, setFeetInput] = useState<string>('')
  const [inchesInput, setInchesInput] = useState<string>('')
  const isInternalUpdate = useRef(false)

  // Sync external value changes to internal state
  useEffect(() => {
    if (isInternalUpdate.current) {
      isInternalUpdate.current = false
      return
    }

    if (value > 0) {
      if (displayMode === 'cm') {
        setCmInput(value.toString())
      } else {
        const { feet, inches } = cmToFeetInches(value)
        setFeetInput(feet.toString())
        setInchesInput(inches.toString())
      }
    } else {
      // Clear all inputs if value is 0
      setCmInput('')
      setFeetInput('')
      setInchesInput('')
    }
  }, [value, displayMode])

  // Handle display mode switch
  const handleDisplayModeChange = (
    _event: React.MouseEvent<HTMLElement>,
    newMode: 'cm' | 'ftin' | null
  ) => {
    if (newMode !== null) {
      setDisplayMode(newMode)
      // Convert current value to new display format
      if (value > 0) {
        if (newMode === 'cm') {
          setCmInput(value.toString())
        } else {
          const { feet, inches } = cmToFeetInches(value)
          setFeetInput(feet.toString())
          setInchesInput(inches.toString())
        }
      }
    }
  }

  // Handle cm input
  const handleCmChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value

    // Allow empty input
    if (inputValue === '') {
      setCmInput('')
      isInternalUpdate.current = true
      onChange(0)
      return
    }

    const val = parseFloat(inputValue)

    // Validate: must be a valid positive number
    if (!isNaN(val) && val >= 0) {
      setCmInput(inputValue)
      isInternalUpdate.current = true
      onChange(Math.round(val))
    }
  }

  // Handle feet input
  const handleFeetChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value

    // Allow empty input
    if (inputValue === '') {
      setFeetInput('')
      const inches = parseFloat(inchesInput) || 0
      const cmVal = feetInchesToCm(0, inches)
      isInternalUpdate.current = true
      onChange(Math.round(cmVal))
      return
    }

    const val = parseInt(inputValue, 10)

    // Validate: must be a valid non-negative integer
    if (!isNaN(val) && val >= 0) {
      setFeetInput(inputValue)
      const inches = parseFloat(inchesInput) || 0
      const cmVal = feetInchesToCm(val, inches)
      isInternalUpdate.current = true
      onChange(Math.round(cmVal))
    }
  }

  // Handle inches input
  const handleInchesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value

    // Allow empty input
    if (inputValue === '') {
      setInchesInput('')
      const feet = parseFloat(feetInput) || 0
      const cmVal = feetInchesToCm(feet, 0)
      isInternalUpdate.current = true
      onChange(Math.round(cmVal))
      return
    }

    let val = parseFloat(inputValue)

    // Validate: must be a valid number between 0 and 11.99
    if (!isNaN(val) && val >= 0) {
      // Cap inches at 11
      if (val > 11) {
        val = 11
      }
      setInchesInput(val.toString())
      const feet = parseFloat(feetInput) || 0
      const cmVal = feetInchesToCm(feet, val)
      isInternalUpdate.current = true
      onChange(Math.round(cmVal))
    }
  }

  return (
    <Box
      display={'flex'}
      flexDirection={'column'}
      flexWrap={'nowrap'}
      width={'100%'}
    >
      <Grid
        container
        alignItems='center'
        spacing={1}
        className={classes.root}
        wrap='nowrap'
      >
        <Grid item className={classes.inputGrid}>
          {displayMode === 'cm' ? (
            <TextField
              label={label}
              type='number'
              value={cmInput}
              onChange={handleCmChange}
              className={classes.input}
              size='small'
              variant='outlined'
              InputLabelProps={{ shrink: true }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position='end'>cm</InputAdornment>
                ),
              }}
              disabled={disabled}
              inputProps={{ min: 0, step: 1 }}
              required={required}
              error={error}
            />
          ) : (
            <>
              <TextField
                label='Feet'
                type='number'
                value={feetInput}
                onChange={handleFeetChange}
                className={classes.input}
                size='small'
                variant='outlined'
                InputLabelProps={{ shrink: true }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position='end'>ft</InputAdornment>
                  ),
                }}
                disabled={disabled}
                inputProps={{ min: 0, step: 1 }}
                required={required}
                error={error}
              />
              <TextField
                label='Inches'
                type='number'
                value={inchesInput}
                onChange={handleInchesChange}
                className={classes.input}
                size='small'
                variant='outlined'
                InputLabelProps={{ shrink: true }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position='end'>in</InputAdornment>
                  ),
                }}
                disabled={disabled}
                inputProps={{ min: 0, max: 11, step: 1 }}
                required={required}
                error={error}
              />
            </>
          )}
        </Grid>
        <Grid item>
          <ToggleButtonGroup
            value={displayMode}
            exclusive
            onChange={handleDisplayModeChange}
            disabled={disabled}
            size='small'
          >
            <ToggleButton
              value='cm'
              size='small'
              className={classes.toggleButton}
            >
              cm
            </ToggleButton>
            <ToggleButton
              value='ftin'
              size='small'
              className={classes.toggleButton}
            >
              ft/in
            </ToggleButton>
          </ToggleButtonGroup>
        </Grid>
      </Grid>
      {helperText && (
        <Typography ml={1} fontSize={'10px'} color={'red !important'}>
          {helperText}
        </Typography>
      )}
    </Box>
  )
}

export default Height
